import axios, { type AxiosError } from 'axios';

import { ApiError, isProblemDocument } from '@/shared/api/errors/errors';

export type TokenProvider = () => Promise<string | null>;
export type AuthErrorHandler = (error: AxiosError) => void;

export interface CreateApiClientOptions {
  baseURL: string;
  getToken: TokenProvider;
  onAuthError?: AuthErrorHandler;
  onAuthSuccess?: () => void;
  defaults?: { params?: Record<string, string> };
}

export function createApiClient(options: CreateApiClientOptions) {
  const { baseURL, getToken, onAuthError, onAuthSuccess, defaults } = options;

  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    params: defaults?.params,
  });

  client.interceptors.request.use(async (requestConfig) => {
    const token = await getToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  });

  client.interceptors.response.use(
    (response) => {
      onAuthSuccess?.();
      return response;
    },
    async (error: AxiosError) => {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        onAuthError?.(error);
      }

      // Convert RFC 7807 problem+json responses to ApiError so consumers can
      // switch on `code` rather than parsing the underlying axios error body.
      // Requests made with responseType 'blob'/'text' skip axios's JSON parsing,
      // so an error body arrives as a Blob or string — recover those too.
      const data = await coerceProblemBody(error);
      if (isProblemDocument(data)) {
        return Promise.reject(
          new ApiError(data, {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
          })
        );
      }

      return Promise.reject(error);
    }
  );

  return client;
}

/** Error body of the failed response, JSON-parsing Blob/string bodies when the
 *  content type says JSON. Falls back to the raw body when parsing fails. */
async function coerceProblemBody(error: AxiosError): Promise<unknown> {
  const data = error.response?.data;
  const contentType = String(error.response?.headers?.['content-type'] ?? '');
  if (contentType.includes('json')) {
    try {
      if (data instanceof Blob) return JSON.parse(await data.text());
      if (typeof data === 'string') return JSON.parse(data);
    } catch {
      // fall through to the raw body
    }
  }
  return data;
}
